import { getGatedIdacBackendUrl } from '../utilities/env';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import Link from '@atlaskit/link';
import PageTitle from '../components/PageTitle';
import SectionMessage from '@atlaskit/section-message';
import queryString from 'query-string';

import { addFlag } from '../actions/flag-actions';
import { Flag } from '../types';
import { isTrelloDesktop } from '../utilities/applications';
import ButtonLink from '../components/ButtonLink';
import EmailSent from '../components/EmailSent';
import messages from './VerifyEmailSentPage.messages';
import WhiteboxPage from '../components/WhiteboxPage';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';

const pageId = 'verifyEmailScreen';

export const VerifyEmailSentPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  const dispatchAddFlag = (flag: Flag) => {
    dispatch(addFlag(flag.type, flag.title, flag.description));
  };

  const email = useAppSelector(state => state.user.email);
  const showBrowserWarning = useAppSelector(state => state.verifyEmailSent.showBrowserWarning);

  usePageViewedEvent(pageId, {
    browserWarningMessageShown: showBrowserWarning,
  });

  const handleResendVerificationEmail = async () => {
    analyticsClient.buttonClickedEvent(pageId, 'resendVerificationEmail');

    try {
      const path = getGatedIdacBackendUrl(`/rest/resend-email${location.search}`);
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 204) {
        addSuccessFlag();
      } else {
        addErrorFlag();
      }
    } catch {
      addErrorFlag();
    }
  };

  const handleReturnToLogin = () => {
    analyticsClient.buttonClickedEvent(pageId, 'verifyEmailReturnLoginLink');
  };

  const addSuccessFlag = () => {
    dispatchAddFlag({
      type: 'success',
      title: messages.verifyEmailFlagSuccessTitle,
      description: messages.verifyEmailFlagSuccessDesc,
    });
  };

  const addErrorFlag = () => {
    dispatchAddFlag({
      type: 'error',
      title: messages.verifyEmailFlagErrorTitle,
      description: messages.verifyEmailFlagErrorDesc,
    });
  };

  const renderWhiteboxFooter = () => {
    const returnLoginLink = `/login?${queryString.stringify({
      ...queryString.parse(location.search),
      email,
    })}`;

    const returnToLoginLink = isTrelloDesktop() ? (
      <Link id="return-login-button" href={returnLoginLink} onClick={handleReturnToLogin}>
        <FormattedMessage id="return.to.log.in" defaultMessage="Return to log in" />
      </Link>
    ) : null;

    return (
      <>
        <ul>
          <li>
            <ButtonLink
              id="send-validation-submit"
              spacing="none"
              className="link-color-override"
              onClick={handleResendVerificationEmail}>
              <FormattedMessage
                id="verify.email.resend.signup.link"
                defaultMessage="Resend signup link"
              />
            </ButtonLink>
          </li>
        </ul>
        {returnToLoginLink && (
          <ul>
            <li>{returnToLoginLink}</li>
          </ul>
        )}
      </>
    );
  };

  return (
    <WhiteboxPage
      // @ts-ignore no id
      id="email-sent-page"
      pageId={pageId}
      header={
        <FormattedMessage
          id="verify.email.title"
          tagName="h5"
          defaultMessage="Check your inbox to finish signup"
        />
      }
      whiteboxFooter={renderWhiteboxFooter()}>
      <PageTitle title={formatMessage(messages.headTitle)} />
      <EmailSent
        email={email}
        message={
          <FormattedMessage
            id="verify.email.sent.signup.link"
            tagName="p"
            defaultMessage="We sent a signup link to:"
            description="Telling user that a verification link has been sent. This message is followed by the user's email address on the next line."
          />
        }
        warningMessage={
          showBrowserWarning && (
            <SectionMessage testId="browser-warning" appearance="information">
              {formatMessage(messages.browserWarning)}
            </SectionMessage>
          )
        }
      />
    </WhiteboxPage>
  );
};

export default VerifyEmailSentPage;
